import React from "react";
import { 
    HiOutlineChartBar, 
    HiOutlineShare, 
    HiOutlineDocumentText, 
    HiOutlineFolder, 
    HiOutlineCloudDownload,
    HiOutlineTrash,
    HiOutlineClock
} from "react-icons/hi";
import { motion } from "framer-motion";
import { useActivity } from "../ActivityContext";

const HistoryPage = () => {
    const { reportData, loadingReport: loading } = useActivity();

    const getActionMeta = (type) => {
        switch (type) {
            case "FILE_UPLOADED": return { icon: <HiOutlineDocumentText />, color: "text-emerald-600", bg: "bg-emerald-50", label: "Uploads" };
            case "FOLDER_CREATED": return { icon: <HiOutlineFolder />, color: "text-amber-600", bg: "bg-amber-50", label: "Folders Created" };
            case "FILE_DOWNLOADED": return { icon: <HiOutlineCloudDownload />, color: "text-indigo-600", bg: "bg-indigo-50", label: "Downloads" };
            case "ACCESS_GRANTED": return { icon: <HiOutlineShare />, color: "text-purple-600", bg: "bg-purple-50", label: "Shares" };
            case "FILE_DELETED": return { icon: <HiOutlineTrash />, color: "text-rose-600", bg: "bg-rose-50", label: "Deleted Files" };
            case "FOLDER_DELETED": return { icon: <HiOutlineTrash />, color: "text-orange-600", bg: "bg-orange-50", label: "Deleted Folders" };
            case "DOWNLOAD_TOKEN_REQUESTED": return { icon: <HiOutlineClock />, color: "text-sky-600", bg: "bg-sky-50", label: "Token Requests" };
            default: return { icon: <HiOutlineClock />, color: "text-slate-500", bg: "bg-slate-50", label: type.replace(/_/g, ' ') };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Never";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex flex-col gap-2">
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight leading-none">Activity History</h1>
                <p className="text-slate-500 font-medium tracking-wide border-l-2 border-indigo-600 pl-4 py-1 mt-3">
                    Aggregated insight into your workspace contributions
                </p>
            </div>

            {loading ? (
                <div className="p-32 flex flex-col items-center justify-center gap-4 text-slate-400">
                    <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
                    <p className="font-bold tracking-widest text-[10px] uppercase">Generating report...</p>
                </div>
            ) : !reportData || reportData.reportData.length === 0 ? (
                <div className="p-32 text-center text-slate-400 flex flex-col items-center gap-4">
                    <HiOutlineChartBar size={48} className="opacity-10" />
                    <p className="font-bold tracking-widest text-[10px] uppercase">No report data available yet</p>
                </div>
            ) : (
                <div className="space-y-12">
                    {/* Hero Stat */}
                    <div className="bg-indigo-600 rounded-[2.5rem] p-10 text-white shadow-2xl shadow-indigo-200 relative overflow-hidden group">
                        <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:scale-125 transition-transform duration-1000" />
                        <div className="relative z-10 space-y-4">
                            <span className="text-indigo-100 font-black uppercase tracking-[0.3em] text-xs">Overall Impact</span>
                            <div className="flex items-baseline gap-4">
                                <h2 className="text-7xl font-black">{reportData.totalActivities}</h2>
                                <span className="text-2xl text-indigo-200 font-bold">Total Operations</span>
                            </div>
                        </div>
                    </div>

                    {/* Report Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {reportData.reportData.map((item, idx) => {
                            const meta = getActionMeta(item.actionType);
                            return (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    key={idx}
                                    className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col gap-6"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className={`p-4 ${meta.bg} ${meta.color} rounded-2xl text-2xl`}>
                                            {meta.icon}
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Metric ID: {idx + 1}</span>
                                    </div>
                                    
                                    <div className="space-y-1">
                                        <h3 className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">{meta.label}</h3>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-4xl font-black text-slate-900">{item.count}</span>
                                            <span className="text-slate-400 font-bold text-xs">Total</span>
                                        </div>
                                    </div>

                                    <div className="mt-auto pt-6 border-t border-slate-100 flex items-center justify-between">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Last Action</span>
                                            <span className="text-xs font-bold text-slate-700">{formatDate(item.lastAction)}</span>
                                        </div>
                                        <div className="w-8 h-1 bg-slate-100 rounded-full" />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="pb-12 text-center">
                <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Workspace report generated on {new Date().toLocaleDateString()}</p>
            </div>
        </div>
    );
};

export default HistoryPage;
